import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Star, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Thumbnail } from './Thumbnail';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ItemActionMenu } from './ItemActionMenu';
import { useItemListContext } from './ItemListProvider';
import { useItemActions } from '@/hooks/useItemActions';
import { useAppDispatch } from '@/store/hooks';
import { updateResourceProjectPreviewStatus } from '../../apiServices/resourceApi';
import { updateItemPreview } from '../../app/features/project/projectSlice';
import type { NormalizedItem } from './types';

interface FileCardProps {
  item: NormalizedItem;
  parentId?: string;
  showPreviewToggle?: boolean;
}

export function FileCard({ item, parentId, showPreviewToggle = false }: FileCardProps) {
  const { isSelected, updateIsQuerying, onRefresh, permissions, updateItem, sortCriteria, sortAscending } = useItemListContext();
  const { isFavorite } = useItemActions({ updateIsQuerying, onRefresh });
  const reduxDispatch = useAppDispatch();
  const params = useParams();
  const selected = isSelected(item.id);
  const favorite = isFavorite(item.id);
  const [toggling, setToggling] = useState(false);

  const parentType = parentId ? 'folder' : 'project';
  const pid = parentId || params.id;
  // Use the current route's viewType so prev/next navigation includes all files in the same view
  const viewType = params.viewType || 'allfiles';
  const sortQuery = `?sortBy=${sortCriteria}&ascending=${sortAscending}`;
  const linkTo = pid
    ? `/in/${parentType}/${pid}/resource/${item.id}/${viewType}${sortQuery}`
    : undefined;

  const canTogglePreview = showPreviewToggle && permissions.canEdit && item.isRendable;

  const handlePreviewToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggling) return;

    // Optimistic: flip immediately
    const newPreview = !item.preview;
    updateItem(item.id, { preview: newPreview });
    reduxDispatch(updateItemPreview(item));
    setToggling(true);

    try {
      await updateResourceProjectPreviewStatus(
        item.id,
        item.name || item.title,
        newPreview,
      );
    } catch {
      // Revert on failure
      updateItem(item.id, { preview: item.preview });
      reduxDispatch(updateItemPreview({ ...item, preview: newPreview }));
    } finally {
      setToggling(false);
    }
  };

  const card = (
    <div
      className={cn(
        'group relative rounded-xl border transition-all',
        selected
          ? 'border-primary bg-primary-muted shadow-md'
          : 'border-border/60 bg-card shadow-sm hover:shadow-md hover:border-border',
      )}
    >
      <Thumbnail item={item} size="lg" className="aspect-video w-full overflow-hidden rounded-t-[11px]" />
      <div className="flex items-center gap-2 px-3 py-2">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {item.title || item.name}
        </p>
        {favorite && <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />}
        {canTogglePreview && (
          <button
            onClick={handlePreviewToggle}
            className={cn(
              'rounded p-1 transition-colors',
              item.preview
                ? 'text-primary hover:bg-primary-muted'
                : 'text-muted-foreground hover:bg-secondary hover:text-muted-foreground',
            )}
            title={item.preview ? 'Visible in main view (click to hide)' : 'Hidden from main view (click to show)'}
          >
            {item.preview ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        )}
        <ItemActionMenu item={item} />
      </div>
      <div className="absolute left-2 top-2">
        <SelectionCheckbox item={item} />
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
